import { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import { Resource, Texture } from "pixi.js";
import { Box, Card, Popper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../../../app/helpers";
import { RootState } from "../../../../../app/rootReducer";
import { BookingInputs } from "../../../Domain/Types/BookingInputs.type";
import { IFloorPayload } from "../../../Domain/Types/FloorPlan/FloorPayload.type";
import { ISeatStatus } from "../../../Domain/Types/FloorPlan/SeatStatus.type";
import { IViewport } from "../../../Domain/Types/FloorPlan/Viewport.type";
import {
  generateBookingPlaceVariant,
  generateZoneVariant
} from "../../../Functions/generateVariant";
import { useGeometry } from "../../../Hooks/useGeometry";
import { DisplayViewport } from "../../DisplayViewport/DisplayViewport";
import { FloorPlanBackground } from "../../FloorPlanBackground/FloorPlanBackground";
import {
  checkUserNumberBooking,
  createPlaceSelectedHandler,
  generateInitial,
  getZoneContainerDisabledForBooking,
  isPlaceClickable,
  isZoneClickable
} from "./Functions/SelectFloorPlanView.functions";
import { tandemBlockedHoverLabel } from "./Functions/tandemBlockedHover.function";
import { runRecommendedFavoriteAutoSelectEffect } from "./Functions/recommendedFavoriteAutoSelect.function";
import { useMousePosition } from "./useMousePosition";
import { FloorAvailabilityWarningData } from "../../../../../features/FloorManager/typings/floor-warningData.entity";
import { calculateWarnRatio } from "../../Zone/ZoneWarnGraphic";
import { ZoneContainer } from "../../Zone/ZoneContainer";
import { PlaceContainer } from "../../Place/PlaceContainer";
import { ResponsiveSelectionPrompt } from "./SelectionPrompt/Responsive-SelectionPrompt";
import { PlanViewMode } from "../../PixiViewport/PixiViewport";
import { ConnectedBookingData } from "../../../../../features/Booking-Form/typings/connected-bookingData";
import {
  compatibilityCircleFill,
  formatFilterCompatibilityLabel,
  getFilterCompatibilityRatio,
  resolveBookingZoneCompatibilityFill,
  shouldApplyFilterCompatibilityTint,
  type FilterCompatibility
} from "../../../../../features/Booking-Form/functions/booking-filter-compatibility.functions";
import { EmptyBackgroundText } from "../../Canvas/Partials/EmptyBackgroundText.partial";
import type { BookingFavoriteDto } from "../../../../../hooks/Remote/BookingFavorites/booking-favorites.types";
import { BOOKING_SELECTION_DRAG_THRESHOLD_PX } from "../../../Domain/Constants/floorPlanBookingTouch.constants";
import {
  getFloorPlanCanvasCorner,
  resolveFloorPlanCanvasSize,
  resolveFloorPlanPlaceScale
} from "../../../Functions/floorPlanCanvas.functions";
import { FilterCompatibilityHoverChip } from "./FilterCompatibilityHoverChip.partial";

interface Props {
  name: string;
  floorPlan: IFloorPayload;
  floorPlanForService: IFloorPayload | undefined;
  seatStatus: ISeatStatus;
  userOccupiedPlaces: number[];
  background: Texture<Resource>;
  isEmptyBgUsed: boolean;
  bookingInputs: BookingInputs;
  userIndex: number;
  warningData: FloorAvailabilityWarningData;
  selectedColleagueFilter: ConnectedBookingData[];
  isOccupiedDataLoading: boolean;
  onSingleSelect?: (placeInventoryId: number) => void;
  onTeamSelect?: (index: number, placeInventoryId: number) => void;
  setUserIndex: (i: number) => void;
  onNextStep: () => void;
  openTimeDialog: (selectedEntryId: number) => void;
  /** Inventory ids marked as user favorites for the current booking mode (floor query) */
  favoriteInventoryIds?: ReadonlySet<number>;
  /** Opens selection prompt for recommended favorite once per floor load */
  recommendedFavorite?: BookingFavoriteDto | null;
  /** When true, skip auto-selecting the recommended favorite (e.g. active floor filters). */
  skipFavoriteAutoSelect?: boolean;
  /** When true, floor-plan zone.disabled does not block selection styling (admin inactive bypass). */
  allowInactiveResourceBooking?: boolean;
  /** Disabled inventory ids admins may book; those desks are drawn as available, then filter-tinted. */
  disabledInventoryIdsForAdminSelection?: number[];
  filterCompatibility?: FilterCompatibility[];
}

export function SelectFloorPlanView({
  children,
  name,
  background,
  isEmptyBgUsed,
  floorPlan,
  floorPlanForService,
  seatStatus,
  userOccupiedPlaces,
  bookingInputs,
  userIndex,
  warningData,
  selectedColleagueFilter,
  isOccupiedDataLoading,
  onSingleSelect,
  onTeamSelect,
  setUserIndex,
  onNextStep,
  openTimeDialog,
  favoriteInventoryIds,
  recommendedFavorite,
  skipFavoriteAutoSelect = false,
  allowInactiveResourceBooking = false,
  disabledInventoryIdsForAdminSelection,
  filterCompatibility
}: PropsWithChildren<Props>) {
  const { t } = useTranslation();

  const { usersBookedFor, bookingType } = useSelector((state: RootState) => state.booking.inputs);
  const { userInformation } = useSelector((state: RootState) => state.login);
  const { convertPixiPoints } = useGeometry();

  const [mouseX, mouseY] = useMousePosition();
  const [dialogPosition, setDialogPosition] = useState<[number, number]>([0, 0]);
  const [selectedPlace, setSelectedPlace] = useState<number | undefined>(undefined);
  const [selectedZone, setSelectedZone] = useState<number | undefined>(undefined);

  const [viewportDimensions, setViewportDimensions] = useState<IViewport>({ width: 0, height: 0 });
  const [placeScale, setPlaceScale] = useState(0);
  const [warnScale, setWarnScale] = useState(0);

  const [isHoverWarning, setIsHoverWarning] = useState(false);
  const [hoverCompatibilityLabel, setHoverCompatibilityLabel] = useState<string | undefined>(
    undefined
  );

  const canvasSize = useMemo(() => {
    if (!background) {
      return undefined;
    }

    return resolveFloorPlanCanvasSize(floorPlan, background);
  }, [background, floorPlan]);

  const [activeTouchCount, setActiveTouchCount] = useState(0);

  const compatibilityByInventoryId = useMemo(() => {
    return new Map((filterCompatibility ?? []).map(entry => [entry.inventoryId, entry]));
  }, [filterCompatibility]);

  const selectedInventoryId = useMemo(() => {
    if (selectedPlace != null) {
      return floorPlan.places.find(place => place.id === selectedPlace)?.inventoryId;
    }
    if (selectedZone != null) {
      return floorPlan.zones.find(zone => zone.id === selectedZone)?.inventory?.id;
    }
    return undefined;
  }, [floorPlan.places, floorPlan.zones, selectedPlace, selectedZone]);

  const warnRef = useRef(null);
  const autoSelectAppliedForFloor = useRef<number | null>(null);
  const lastAutoSelectFloorInventoryIdRef = useRef<number | null>(null);
  const prevSkipFavoriteAutoSelectRef = useRef(skipFavoriteAutoSelect);

  useEffect(() => {
    const floorInventoryId = floorPlan.floorInventoryId;
    if (lastAutoSelectFloorInventoryIdRef.current !== floorInventoryId) {
      lastAutoSelectFloorInventoryIdRef.current = floorInventoryId;
      autoSelectAppliedForFloor.current = null;
    }
    if (prevSkipFavoriteAutoSelectRef.current && !skipFavoriteAutoSelect) {
      autoSelectAppliedForFloor.current = null;
    }
    prevSkipFavoriteAutoSelectRef.current = skipFavoriteAutoSelect;
    runRecommendedFavoriteAutoSelectEffect(
      {
        recommendedFavorite,
        bookingType,
        floorPlan,
        autoSelectAppliedForFloorId: autoSelectAppliedForFloor.current,
        seatStatus,
        skipFavoriteAutoSelect
      },
      {
        setSelectedPlace,
        setSelectedZone,
        setAutoSelectAppliedForFloorId: floorId => {
          autoSelectAppliedForFloor.current = floorId;
        }
      }
    );
  }, [
    favoriteInventoryIds,
    recommendedFavorite,
    floorPlan,
    bookingType,
    seatStatus,
    skipFavoriteAutoSelect
  ]);

  useEffect(() => {
    if (!background) return;
    setPlaceScale(resolveFloorPlanPlaceScale(floorPlan, background));
  }, [background, floorPlan]);

  useEffect(() => {
    if (!canvasSize) return;
    const { width: vdWidth, height: vdHeight } = viewportDimensions;

    if (!vdWidth || !vdHeight) return;

    const ratio = calculateWarnRatio(vdWidth, vdHeight, canvasSize.width, canvasSize.height);
    setWarnScale(ratio);
  }, [canvasSize, viewportDimensions]);

  useEffect(() => {
    if (isHoverWarning) setDialogPosition([mouseX, mouseY]);
  }, [isHoverWarning, mouseX, mouseY]);

  return (
    <>
      {background && !!placeScale && canvasSize && (
        <>
          <DisplayViewport
            planViewMode={PlanViewMode.BOOKING}
            floorPlanName={name}
            initialZoomEnd={getFloorPlanCanvasCorner(canvasSize)}
            currentFloorPlan={undefined}
            placeScale={placeScale}
            viewportDimensions={viewportDimensions}
            setViewportDimensions={setViewportDimensions}
            setActiveTouchCount={setActiveTouchCount}
          >
            {!isEmptyBgUsed && <FloorPlanBackground texture={background} floorPlan={floorPlan} />}
            {isEmptyBgUsed && (
              <EmptyBackgroundText
                rectWidth={floorPlan.viewport.width}
                rectHeight={floorPlan.viewport.height}
                posY={5}
              />
            )}

            {/** draw zones */}
            {floorPlan.zones.map(zone => {
              const zoneVariant = generateZoneVariant(zone, seatStatus);
              const zoneCompatibility =
                zone.inventory?.id != null
                  ? compatibilityByInventoryId.get(zone.inventory.id)
                  : undefined;
              const zoneCompatibilityRatio = getFilterCompatibilityRatio(zoneCompatibility);
              const zoneCompatibilityFill = resolveBookingZoneCompatibilityFill(
                zoneVariant,
                zone.inventory?.zoneTypeId,
                zoneCompatibilityRatio
              );

              return (
                <ZoneContainer
                  id={zone.id}
                  key={zone.id}
                  inventoryId={zone.inventory?.id}
                  walls={convertPixiPoints(zone.coordinates)}
                  disabled={getZoneContainerDisabledForBooking(
                    zone.disabled,
                    allowInactiveResourceBooking
                  )}
                  zoneType={zone.inventory?.zoneTypeId as number}
                  onClick={id => setSelectedZone(id)}
                  isClickable={isZoneClickable({
                    bookingType,
                    zoneTypeId: zone.inventory?.zoneTypeId,
                    zoneInventoryId: zone.inventory?.id as number,
                    seatStatus,
                    allowInactiveResourceBooking
                  })}
                  isSelected={zone.id === selectedZone}
                  showWarning={checkUserNumberBooking(
                    bookingType,
                    zone.inventory?.minUsers,
                    zone.inventory?.maxUsers,
                    bookingInputs.zoneAccess,
                    warningData
                  )}
                  isFavorite={
                    favoriteInventoryIds != null &&
                    zone.inventory?.id != null &&
                    favoriteInventoryIds.has(zone.inventory.id)
                  }
                  setIsHoverWarning={(h: boolean) => setIsHoverWarning(h)}
                  onPointerEnter={() => {
                    const blockedLabel = tandemBlockedHoverLabel(
                      zone.inventory?.id,
                      seatStatus,
                      "zone",
                      t
                    );
                    if (blockedLabel) {
                      setHoverCompatibilityLabel(blockedLabel);
                      return;
                    }
                    const label = formatFilterCompatibilityLabel(zoneCompatibility, t);
                    if (label) {
                      setHoverCompatibilityLabel(label);
                    }
                  }}
                  onPointerLeave={() => setHoverCompatibilityLabel(undefined)}
                  activeTouchCount={activeTouchCount}
                  dragThresholdPx={BOOKING_SELECTION_DRAG_THRESHOLD_PX}
                  tool={"SELECT.BOOK"}
                  warnScale={warnScale}
                  variant={zoneVariant}
                  colorOverlay={zoneCompatibilityFill}
                />
              );
            })}

            {/** draw places */}
            {floorPlan.places.map(workplace => {
              const inv = workplace.inventoryId || workplace.inventory?.id;
              const isFavoritePlace =
                favoriteInventoryIds != null && inv != null && favoriteInventoryIds.has(inv);
              const placeVariant = generateBookingPlaceVariant(
                { workplace: workplace, zone: undefined },
                seatStatus,
                bookingType
              );
              const compatibility = inv != null ? compatibilityByInventoryId.get(inv) : undefined;
              const compatibilityRatio = getFilterCompatibilityRatio(compatibility);
              const compatibilityFill =
                shouldApplyFilterCompatibilityTint(placeVariant) && compatibilityRatio != null
                  ? compatibilityCircleFill(compatibilityRatio)
                  : undefined;
              const compatibilityLabel = formatFilterCompatibilityLabel(compatibility, t);
              return (
                <PlaceContainer
                  key={workplace.id}
                  boundingBox={{
                    height: workplace.inventory?.boundingBox?.height ?? 80,
                    width: workplace.inventory?.boundingBox?.width ?? 160
                  }}
                  inventoryId={workplace.inventoryId || workplace.inventory?.id}
                  id={workplace.id}
                  position={workplace.position}
                  rotation={workplace.rotate}
                  isHighlighted={isFavoritePlace}
                  isFavorite={isFavoritePlace}
                  isSelected={workplace.id === selectedPlace}
                  onClick={createPlaceSelectedHandler(setSelectedPlace, workplace.id)}
                  isSelectable={isPlaceClickable(
                    bookingType,
                    workplace.placeTypeId || workplace.inventory?.placeTypeId || 0,
                    workplace.inventoryId || workplace.inventory?.id,
                    seatStatus
                  )}
                  variant={placeVariant}
                  compatibilityFill={compatibilityFill}
                  onPointerEnter={() => {
                    const blockedLabel = tandemBlockedHoverLabel(inv, seatStatus, "place", t);
                    if (blockedLabel) {
                      setHoverCompatibilityLabel(blockedLabel);
                      return;
                    }
                    if (compatibilityFill != null && compatibilityLabel) {
                      setHoverCompatibilityLabel(compatibilityLabel);
                    }
                  }}
                  onPointerLeave={() => setHoverCompatibilityLabel(undefined)}
                  icon={generateInitial(
                    name,
                    usersBookedFor,
                    userInformation,
                    selectedColleagueFilter,
                    workplace.inventoryId || workplace.inventory?.id,
                    userOccupiedPlaces.includes(workplace.inventoryId)
                  )}
                  isColleaguesPlace={selectedColleagueFilter.some(
                    col => col.bookingInventoryId === workplace.inventoryId
                  )}
                  placeTypeId={workplace.placeTypeId || workplace.inventory?.placeTypeId}
                  placeScale={placeScale}
                  activeTouchCount={activeTouchCount}
                  dragThresholdPx={BOOKING_SELECTION_DRAG_THRESHOLD_PX}
                  tool={"SELECT.BOOK"}
                />
              );
            })}

            {children}
          </DisplayViewport>

          {/** selection prompt dialog */}
          {(selectedPlace !== undefined || selectedZone !== undefined) && (
            <ResponsiveSelectionPrompt
              floorPlan={floorPlan}
              floorPlanForService={floorPlanForService}
              seatStatus={seatStatus}
              selectedPlace={selectedPlace}
              selectedZone={selectedZone}
              bookingInputs={bookingInputs}
              userIndex={userIndex}
              isOccupiedDataLoading={isOccupiedDataLoading}
              setSelectedPlace={setSelectedPlace}
              setSelectedZone={setSelectedZone}
              setUserIndex={setUserIndex}
              onNextStep={onNextStep}
              onSingleSelect={onSingleSelect}
              onTeamSelect={onTeamSelect}
              openTimeDialog={openTimeDialog}
              allowInactiveResourceBooking={allowInactiveResourceBooking}
              disabledInventoryIdsForAdminSelection={disabledInventoryIdsForAdminSelection}
              filterCompatibility={
                selectedInventoryId != null
                  ? compatibilityByInventoryId.get(selectedInventoryId)
                  : undefined
              }
            />
          )}

          {/** warning info */}
          {isHoverWarning && !selectedZone && (
            <>
              <Box
                ref={warnRef}
                sx={{ position: "fixed", top: dialogPosition[1], left: dialogPosition[0] + 20 }}
              />
              {warnRef.current && (
                <Popper
                  id={warnRef.current as string}
                  anchorEl={warnRef.current}
                  open
                  nonce={undefined}
                >
                  <Card sx={{ p: 2, maxWidth: 400 }}>
                    <Typography variant={"body2"}>
                      {t("WarningUserNumbersForConferenceZone")}
                    </Typography>
                  </Card>
                </Popper>
              )}
            </>
          )}

          {hoverCompatibilityLabel && !selectedPlace && !selectedZone && (
            <FilterCompatibilityHoverChip
              label={hoverCompatibilityLabel}
              mouseX={mouseX}
              mouseY={mouseY}
            />
          )}
        </>
      )}
    </>
  );
}
